// eslint-disable-next-line import/no-extraneous-dependencies
import { EmotionJSX } from '@emotion/react/types/jsx-namespace'
import { Image } from '@graphcommerce/image'
import { TabContext, TabPanel } from '@mui/lab'
import { Paper, Tab, Tabs, styled } from '@mui/material'
import { ReactElement, useContext, useEffect, useState } from 'react'
import accountInfoIcon from '../../assets/images/account-account-info-icon.svg'
import addressesIcon from '../../assets/images/account-addresses-icon.svg'
import ordersIcon from '../../assets/images/account-orders-icon.svg'
import rolesIcon from '../../assets/images/account-roles-icon.svg'
import usersIcon from '../../assets/images/account-users-icon.svg'
import missingImageIcon from '../../assets/images/missing-image-icon.svg'
import AccountAddressesPage from '../../pages/account/addresses'
import AccountNamePage from '../../pages/account/name'
import AccountOrdersPage from '../../pages/account/orders'
import SubRolesPage from '../../pages/account/sub-roles'
import SubUserPage from '../../pages/account/sub-users'
import { globalContext } from '../NextUi/globalContext'

type AccountPageTabsProps = {
  className?: string
  defaultTabIndex?: string | undefined
}

export interface VerticalTabProps {
  label: string
  id: string
  value: string
  'aria-controls': string
  icon?: ReactElement<any>
}

interface tabItemProps {
  label: string
  icon?: any
  visible?: boolean
  onClick?: () => void
  component?: EmotionJSX.Element
}

export const VerticalTab = styled((props: VerticalTabProps) => (
  <Tab disableRipple {...props} icon={props.icon} />
))(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyItems: 'start',
  justifyContent: 'start',
  gap: 20,
  minHeight: 56,
  marginBottom: 0,
  whiteSpace: 'nowrap',
  color: 'rgba(0, 0, 0, 0.6)',
  'img.MuiTab-iconWrapper': {
    marginBottom: 0,
    height: 24,
    width: 24,
    fill: '#000000',
    fillOpacity: '0.6',
  },
  '&.Mui-selected img.MuiTab-iconWrapper': {
    fill: '#2196F3',
  },
  ':hover': {
    backgroundColor: 'rgba(97,51,148,0.1)',
  },
}))

export interface StyledTabPanelProps {
  value: string
  children?: React.ReactNode
}

export const StyledTabPanel = styled((props: StyledTabPanelProps) => <TabPanel {...props} />)(
  () => ({
    '&.MuiTabPanel-root': {
      padding: 0,
    },
  }),
)

export function AccountPageTabs(props: AccountPageTabsProps) {
  const { className, defaultTabIndex } = props

  const [value, setValue] = useState(defaultTabIndex ?? '0')
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }
  const a11yProps = (index: number) => ({
    id: `simple-vertical-tab-${index}`,
    'aria-controls': `simple-vertical-tabpanel-${index}`,
  })
  const { subUserCan } = useContext(globalContext)
  const handleClick = () => {}

  const tabItems: tabItemProps[] = [
    { label: 'ORDERS', icon: ordersIcon, visible: true, component: <AccountOrdersPage /> },
    {
      label: 'ADDRESSES',
      icon: addressesIcon,
      visible: subUserCan('address-book'),
      component: <AccountAddressesPage />,
    },
    { label: 'ACCOUNT INFO', icon: accountInfoIcon, visible: true, component: <AccountNamePage /> },
    {
      label: 'USERS',
      icon: usersIcon,
      visible: subUserCan('manage-sub-users-and-roles'),
      component: <SubUserPage />,
    },
    {
      label: 'ROLES',
      icon: rolesIcon,
      visible: subUserCan('manage-sub-users-and-roles'),
      component: <SubRolesPage />,
    },
  ]

  useEffect(() => {
    window.scrollTo(0, 0)
  })

  useEffect(() => {
    if (defaultTabIndex) {
      setValue(defaultTabIndex)
    } else {
      setValue('0')
    }
  }, [defaultTabIndex])

  return (
    <div className={`w-full lg:max-w-maxContentWidth ${className}`}>
      <TabContext value={value}>
        <div className='flex flex-col md:grid md:grid-cols-[25fr_75fr] md:gap-10'>
          <Paper
            elevation={3}
            className='md:max-width-auto sticky top-[113px] flex w-full max-w-full items-center justify-center md:top-[56px] md:block md:h-[600px] md:max-h-[600px]'
          >
            <Tabs
              value={value}
              onChange={handleChange}
              orientation='vertical'
              variant='scrollable'
              sx={{
                width: '100%',
                display: 'flex',
                minWidth: 200,
              }}
              id='verticalTabsDesktop'
              name='verticalTabsDesktop'
            >
              {tabItems
                ?.filter((i) => i.visible)
                ?.map((item, index) => (
                  <VerticalTab
                    label={item.label}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    icon={
                      item?.icon ? (
                        <Image
                          unoptimized
                          src={item?.icon}
                          height={30}
                          width={30}
                          className='flex h-[24px] w-[24px] items-center justify-center'
                        />
                      ) : (
                        <Image
                          unoptimized
                          src={missingImageIcon}
                          height={30}
                          width={30}
                          className='flex h-[24px] w-[24px] items-center justify-center rounded-[50%]'
                        />
                      )
                    }
                    value={index.toString()}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    {...a11yProps(index)}
                  />
                ))}
            </Tabs>
            <Tabs
              value={value}
              onChange={handleChange}
              orientation='horizontal'
              variant='scrollable'
              scrollButtons
              allowScrollButtonsMobile
              sx={{
                bgcolor: '#FFFFFF',
                zIndex: 10,
              }}
              id='horizontalTabsMobile'
              name='horizontalTabsMobile'
            >
              {tabItems
                ?.filter((i) => i.visible)
                ?.map((item, index) => (
                  <VerticalTab
                    label={item.label}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument

                    value={index.toString()}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    {...a11yProps(index)}
                  />
                ))}
            </Tabs>
          </Paper>
          <div className='m-5 md:my-5 md:mr-10'>
            {tabItems.map((item, index) => (
              <StyledTabPanel value={index.toString()}>{item.component}</StyledTabPanel>
            ))}
          </div>
        </div>
      </TabContext>
    </div>
  )
}
